/**
 * Data Validation Stage - Stage 1
 * Shows extracted fields for validation before processing
 * Dynamically builds fields from analyzer configuration
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { useTheme } from '@mui/material/styles';
import ValidationForm from '../ValidationForm';
import ActionButtons from '../ActionButtons';
import HoldDialog from '../HoldDialog';
import KeyboardShortcutsDialog from '../KeyboardShortcutsDialog';
import DeedsReviewPanel from '../DeedsReviewPanel';
import ExemptionsReviewPanel from '../ExemptionsReviewPanel';
import AddressReviewPanel, { ADDRESS_SUB_FIELD_SUFFIXES, AddressFieldGroup } from '../AddressReviewPanel';
import { useValidation, useHighlighting } from '../../../contexts';
import analyzerService, { AnalyzerConfiguration } from '../../../services/analyzerService';
import { CONFIDENCE_HIGH_THRESHOLD } from '../../../config/validation';
import { focusFieldInput } from '../../../utils/fieldFocus';

/** True when typing into the target would be expected (don't hijack `?` there). */
function isTypingTarget(target: HTMLElement | null): boolean {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

interface DataValidationStageProps {
  extractedData: any;
  documentId: string;
  /** May be async (StageManager finalizes zero-output docs server-side) — awaited with an in-flight guard. */
  onSubmit: (data: any) => void | Promise<void>;
}

export default function DataValidationStage({
  extractedData,
  documentId,
  onSubmit
}: DataValidationStageProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const validationContext = useValidation();
  const highlightingContext = useHighlighting();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [analyzerConfig, setAnalyzerConfig] = useState<AnalyzerConfiguration | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const documentType: string = extractedData?.metadata?.documentType || '';

  // Load analyzer configuration
  useEffect(() => {
    async function loadAnalyzerConfig() {
      if (!extractedData?.metadata?.documentType) return;

      try {
        const configs = await analyzerService.getAnalyzerConfigurations();
        const config = configs.find(c => c.documentType === extractedData.metadata.documentType);
        setAnalyzerConfig(config || null);
      } catch (error) {
        console.error('Failed to load analyzer configuration:', error);
      }
    }

    loadAnalyzerConfig();
  }, [extractedData?.metadata?.documentType]);

  useEffect(() => {
    validationContext.actions.loadDocumentData(extractedData);
  }, [extractedData]);

  // Track the last viewport+fields combo highlights were initialized for, to skip redundant
  // showAllFields calls on re-renders that don't actually change either (e.g. unrelated parent
  // re-renders triggering this effect via a freshly-built `actions` object reference).
  const lastInitializedRef = useRef<{ viewport: typeof highlightingContext.state.pdfViewport; fields: typeof extractedData.fields } | null>(null);

  useEffect(() => {
    const viewport = highlightingContext.state.pdfViewport;
    const fields = extractedData.fields;

    if (!fields || !viewport) {
      return;
    }

    const last = lastInitializedRef.current;
    if (last && last.viewport === viewport && last.fields === fields) {
      return;
    }
    lastInitializedRef.current = { viewport, fields };

    const fieldsWithBoundingBoxes = fields
      .filter((field: any) => field.boundingBoxes && field.boundingBoxes.length > 0)
      .map((field: any) => ({ id: field.id, boundingBoxes: field.boundingBoxes }));

    if (fieldsWithBoundingBoxes.length > 0) {
      highlightingContext.actions.showAllFields(fieldsWithBoundingBoxes);
    }
  }, [extractedData.fields, highlightingContext.state.pdfViewport, highlightingContext.actions.showAllFields]);

  const handleFieldNavigation = useCallback((direction: 'next' | 'previous') => {
    const fields = extractedData.fields;
    const currentFieldIndex = fields.findIndex(
      (field: any) => field.id === highlightingContext.state.activeFieldId
    );

    let nextIndex: number;
    if (direction === 'next') {
      nextIndex = currentFieldIndex < fields.length - 1 ? currentFieldIndex + 1 : 0;
    } else {
      nextIndex = currentFieldIndex > 0 ? currentFieldIndex - 1 : fields.length - 1;
    }

    const nextField = fields[nextIndex];
    if (nextField) {
      highlightingContext.actions.setActiveField(nextField.id, nextField.boundingBoxes);
      
      const fieldElement = document.getElementById(`field-${nextField.id}`);
      if (fieldElement && scrollContainerRef.current) {
        fieldElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [extractedData.fields, highlightingContext]);

  // Editable HITL fields (enabled, visible, not read-only) ordered lowest confidence first —
  // the keyboard traversal order (worst extraction gets attention first), tie-broken by the
  // configured display order so equal-confidence fields still follow the document flow.
  const confidenceOrderedFields = useMemo(() => {
    const fields = validationContext.state.document?.fields ?? [];
    if (!analyzerConfig) return [];
    const configByName = new Map(
      analyzerConfig.fields
        .filter((f) => f.enabled && f.uiGroup !== 'hidden' && !f.readOnly)
        .map((f) => [f.fieldName, f])
    );
    return fields
      .filter((f) => configByName.has(f.name))
      .sort(
        (a, b) =>
          a.confidence - b.confidence ||
          (configByName.get(a.name)!.displayOrder || 0) - (configByName.get(b.name)!.displayOrder || 0)
      );
  }, [analyzerConfig, validationContext.state.document]);

  const reviewedFields = validationContext.state.reviewedFields;
  const { confirmField } = validationContext.actions;

  // Tab/Shift+Tab inside the field list walks fields in confidence order. No wrap — but the
  // ends must EXIT the list explicitly (forward → action buttons, backward → the shortcuts
  // toolbar button): native Tab would land on a DOM-adjacent field input and re-enter the
  // walk, making Save/Reset keyboard-unreachable (WCAG 2.1.1/2.1.2).
  const handleTabTraversal = useCallback(
    (currentFieldId: string, backwards: boolean): boolean => {
      const idx = confidenceOrderedFields.findIndex((f) => f.id === currentFieldId);
      if (idx === -1) return false; // not part of the traversal — let native Tab handle it

      const dir = backwards ? -1 : 1;
      for (let i = idx + dir; i >= 0 && i < confidenceOrderedFields.length; i += dir) {
        if (focusFieldInput(confidenceOrderedFields[i].id)) return true;
      }

      const exit = backwards
        ? document.querySelector<HTMLElement>('[data-hitl-shortcuts-button]')
        : document.querySelector<HTMLElement>('[data-hitl-actions] button:not([disabled])');
      if (exit) {
        exit.focus();
        return true;
      }
      return false;
    },
    [confidenceOrderedFields]
  );

  // Enter = confirm the field, then jump to the next unreviewed field (confidence order,
  // wrapping once). When everything is reviewed, land on Submit; if Submit can't take focus
  // (in-flight), fall back to the required-fields alert so focus always lands somewhere visible.
  const confirmAndAdvance = useCallback(
    (currentFieldId: string) => {
      confirmField(currentFieldId);
      const ordered = confidenceOrderedFields;
      const idx = ordered.findIndex((f) => f.id === currentFieldId);
      for (let step = 1; step <= ordered.length; step++) {
        const candidate = ordered[(idx + step) % ordered.length];
        if (candidate.id !== currentFieldId && !reviewedFields[candidate.id] && focusFieldInput(candidate.id)) {
          return;
        }
      }
      const submitEl = document.querySelector<HTMLButtonElement>('[data-hitl-submit]');
      if (submitEl && !submitEl.disabled) {
        submitEl.focus();
        return;
      }
      document.querySelector<HTMLElement>('[data-hitl-required-alert]')?.focus();
    },
    [confidenceOrderedFields, reviewedFields, confirmField]
  );

  // Confidence-driven starting focus (UX4): once the document + analyzer config are ready, put
  // the analyst on the lowest-confidence field so review starts where attention is needed most.
  const autoFocusedDocRef = useRef<string | null>(null);
  useEffect(() => {
    if (confidenceOrderedFields.length === 0) return;
    if (autoFocusedDocRef.current === documentId) return;
    autoFocusedDocRef.current = documentId;
    // If the analyst already started working (slow config load), never steal their focus.
    if (scrollContainerRef.current?.contains(document.activeElement)) return;
    // Defer one frame so the form has painted its inputs.
    const raf = requestAnimationFrame(() => focusFieldInput(confidenceOrderedFields[0].id));
    return () => cancelAnimationFrame(raf);
  }, [confidenceOrderedFields, documentId]);

  // handleSubmit is recreated per render (it closes over form state); route the keyboard
  // shortcut through a ref so the listener effect doesn't re-attach every render.
  const submitRef = useRef<() => void>(() => {});
  useEffect(() => {
    submitRef.current = handleSubmit;
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      // IME composition commits its own Enter — never hijack it.
      if (event.isComposing) return;

      // Modal dialogs own the keyboard while open (shortcut overlay, hold dialog, submit
      // confirmation) — the workspace shortcuts must not fire behind them.
      if (shortcutsOpen || holdOpen || target.closest?.('[role="dialog"]')) return;

      // `?` opens the shortcut reference from anywhere on the stage (except while typing).
      if (event.key === '?' && !isTypingTarget(target)) {
        event.preventDefault();
        setShortcutsOpen(true);
        return;
      }

      // Ctrl+Enter submits the validated document.
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        submitRef.current();
        return;
      }

      const isInValidationPanel = scrollContainerRef.current?.contains(target);
      if (!isInValidationPanel) return;

      // Field-level shortcuts only apply when focus is inside a HighlightableField anchor.
      const fieldAnchor = target.closest?.('[data-field-id]') as HTMLElement | null;
      const fieldId = fieldAnchor?.dataset.fieldId;

      switch (event.key) {
        case 'Tab':
          if (fieldId && handleTabTraversal(fieldId, event.shiftKey)) {
            event.preventDefault();
          }
          break;
        case 'Enter':
          if (fieldId) {
            event.preventDefault();
            confirmAndAdvance(fieldId);
          }
          break;
        case 'ArrowDown':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleFieldNavigation('next');
          }
          break;
        case 'ArrowUp':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleFieldNavigation('previous');
          }
          break;
        case 'Escape':
          event.preventDefault();
          highlightingContext.actions.clearHighlights();
          (document.activeElement as HTMLElement)?.blur();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleFieldNavigation, highlightingContext, handleTabTraversal, confirmAndAdvance, shortcutsOpen, holdOpen]);

  // Check for missing required fields
  const missingRequiredFields = useMemo(() => {
    if (!analyzerConfig || !validationContext.state.document) return [];

    const requiredFieldConfigs = analyzerConfig.fields.filter(
      f => f.required && f.enabled && f.uiGroup !== 'hidden'
    );

    const missing: string[] = [];
    requiredFieldConfigs.forEach(fieldConfig => {
      // Find the field in document by name to get its id
      const field = validationContext.state.document?.fields.find(f => f.name === fieldConfig.fieldName);
      if (!field) return;

      // Check validatedFields using field.id (not field.name)
      const fieldValue = validationContext.state.validatedFields[field.id];
      const isEmpty = !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '');
      if (isEmpty) {
        missing.push(fieldConfig.displayName || fieldConfig.fieldName);
      }
    });

    return missing;
  }, [analyzerConfig, validationContext.state.document, validationContext.state.validatedFields]);

  const handleSubmit = async () => {
    if (submitting) return; // in-flight guard — a second click must not double-submit

    // Check required fields
    if (missingRequiredFields.length > 0) {
      alert(`Please fill in all required fields:\n\n${missingRequiredFields.join('\n')}`);
      return;
    }

    // Check if user made changes
    const hasChanges = validationContext.state.isDirty;
    
    // If changes exist, save to Cosmos first
    if (hasChanges) {
      try {
        await validationContext.actions.submitValidation();
        // Wait a moment for save to complete
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Failed to save changes:', error);
        // Show error but continue with next stage using current values
      }
    }

    // Build validated data object from state
    // Text formatting has already been applied in ValidationContext
    const validatedData: Record<string, any> = {
      documentId,
      metadata: extractedData.metadata,
      fields: validationContext.state.document?.fields || [],
      validatedFields: validationContext.state.validatedFields
    };

    console.log('DataValidationStage: Passing validated data to next stage:', validatedData);

    // Pass validated data to next stage (no post-processing); awaited so the Submit
    // button reflects the in-flight server call and can't double-fire.
    setSubmitting(true);
    try {
      await onSubmit(validatedData);
    } finally {
      setSubmitting(false);
    }
  };

  // Reviewed = explicitly confirmed (Enter) or edited this session — counted over the editable
  // field set, so the progress line reflects real reviewer work (UX4).
  const totalFields = confidenceOrderedFields.length;
  const completedFields = confidenceOrderedFields.filter((f) => reviewedFields[f.id]).length;
  const lowConfidenceFields = confidenceOrderedFields.filter(
    (f) => f.confidence < CONFIDENCE_HIGH_THRESHOLD
  ).length;

  // Per-analyzer domain review (Sprint M4 Deeds, generalized in M5). Legacy top-level `deeds` is
  // accepted as a fallback when `enrichments` isn't present.
  const enrichments = extractedData?.enrichments
    ?? (extractedData?.deeds ? { deeds: extractedData.deeds } : undefined);

  // Address synthetic sub-fields (Sprint F3, surfaced in G4): for each field opted into
  // addressValidate, find its parent + the 7 `{field}_suffix` sub-fields the backend wrote
  // into document.fields, so AddressReviewPanel can show them read-only to the reviewer.
  const addressFieldGroups = useMemo<AddressFieldGroup[]>(() => {
    const fields = validationContext.state.document?.fields;
    if (!analyzerConfig || !fields) return [];

    const groups: AddressFieldGroup[] = [];
    analyzerConfig.fields
      .filter(fc => fc.addressValidate?.enabled)
      .forEach(fc => {
        const parentField = fields.find(f => f.name === fc.fieldName);
        if (!parentField) return;

        const subFields: AddressFieldGroup['subFields'] = {};
        ADDRESS_SUB_FIELD_SUFFIXES.forEach(suffix => {
          const subField = fields.find(f => f.name === `${fc.fieldName}${suffix}`);
          if (subField) subFields[suffix] = subField;
        });

        if (Object.keys(subFields).length > 0) {
          groups.push({ parentField, subFields });
        }
      });

    return groups;
  }, [analyzerConfig, validationContext.state.document]);

  return (
    <Paper
      elevation={1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Toolbar variant="dense" sx={{ minHeight: 48, borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="h2" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Validate
          </Typography>
          {totalFields > 0 && (
            <Typography variant="caption" color="text.secondary" aria-live="polite">
              {completedFields} of {totalFields} fields reviewed
              {lowConfidenceFields > 0 && (
                <span style={{ color: theme.palette.warning.main, marginLeft: 8 }}>
                  • {lowConfidenceFields} flagged
                </span>
              )}
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<PauseCircleOutlineIcon />}
          onClick={() => setHoldOpen(true)}
          sx={{ mr: 1 }}
        >
          Put on hold
        </Button>
        <Tooltip title="Keyboard shortcuts (?)">
          <IconButton
            aria-label="Keyboard shortcuts"
            data-hitl-shortcuts-button
            onClick={() => setShortcutsOpen(true)}
            size="small"
          >
            <KeyboardIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>

      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          px: 2,
          py: 1,
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100',
            borderRadius: 1,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.400',
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'grey.500',
            },
          },
          '&::after': {
            content: '""',
            display: 'block',
            height: theme.spacing(8),
          }
        }}
      >
        {missingRequiredFields.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} tabIndex={-1} data-hitl-required-alert>
            <Typography variant="body2" fontWeight={600}>
              Required fields missing:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {missingRequiredFields.map(fieldName => (
                <li key={fieldName}>{fieldName}</li>
              ))}
            </Typography>
          </Alert>
        )}
        
        {/* Renders one panel per analyzer that enriched this document. Overrides persist through
            the standard correction path on submit. Adding analyzer #3 = its panel + one line here. */}
        {enrichments?.deeds && <DeedsReviewPanel deeds={enrichments.deeds} />}
        {enrichments?.exemptions && <ExemptionsReviewPanel exemptions={enrichments.exemptions} />}
        <AddressReviewPanel addressFields={addressFieldGroups} />

        <ValidationForm />
      </Box>

      <Divider />

      <Box sx={{ p: 2, flexShrink: 0 }}>
        <ActionButtons onSubmitOverride={handleSubmit} submitting={submitting} />
      </Box>

      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {/* Put on hold — parks the document with a comment for a named person, then leaves the
          workspace (the doc is no longer in this analyst's active review). */}
      <HoldDialog
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        documentId={documentId}
        documentType={documentType}
        onHeld={() => {
          setHoldOpen(false);
          navigate('/');
        }}
      />
    </Paper>
  );
}
